// Added by Sejune Oh on 2019-03-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactNode, CSSProperties, FC, useState, useEffect } from 'react'
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core'
import { useDropzone } from 'react-dropzone'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { ReactSyntheticEvent, ReactEmptyEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime Elements
import {
    Button,
    Dialog as DialogElement,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    InsertDriveFileIcon
} from '../../_primes'
import { GREY_COLOR } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface DialogBaseFCProps {
    title?: string
    description?: string
    confirmBtn?: string
    cancelBtn?: string
    hideCancelBtn?: boolean
    disableBackdropClick?: boolean
    isRemove?: boolean
    customBtns?: JSX.Element[]
    onFileDrop?: (files: any) => void
    buttonType?: 'task' | 'default' | 'danger'
}

interface DialogFCProps extends WithStyles<typeof styles> {
    isOpen: boolean
    onClose: ReactSyntheticEvent
    onConfirmBtnClick: ReactEmptyEvent
    onCancelBtnClick: ReactEmptyEvent
    dialogInfo: DialogBaseFCProps
    children?: ReactNode
    dialogPaperStyle?: CSSProperties
    confirmDisabled: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const Dialog: FC<DialogFCProps> = ({ classes, dialogPaperStyle, ...props }) => {
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // for file drop
    const [ hasFileDrop, setHasFileDrop ] = useState<boolean>(!!props.dialogInfo.onFileDrop)
    const [ importedFileName, setImportedFileName ] = useState<string | undefined>(undefined)

    const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
        onDrop: props.dialogInfo.onFileDrop,
        accept: 'application/json',
        noDrag: true,
        noClick: true
     })

    useEffect(() => {
        setHasFileDrop(!!props.dialogInfo.onFileDrop)
    }, [props.dialogInfo.onFileDrop])

    useEffect(() => {
        if (acceptedFiles.length !== 0) setImportedFileName(acceptedFiles[0].name)
        return () => {
            setImportedFileName(undefined)
        }
    }, [acceptedFiles])
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    let description: string[] = []

    if (props.dialogInfo && props.dialogInfo.description) {
        description = props.dialogInfo.description.split('/n')
    }

    return (
        <DialogElement
            maxWidth={'lg'}
            className={classes.DialgWrapper}
            open={props.isOpen}
            onClose={props.onClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
            PaperProps={{
                className: classes.DialgPaperWrapper,
                style: { ...dialogPaperStyle },
            }}
            disableBackdropClick={props.dialogInfo.disableBackdropClick}
        >
            {props.isOpen && (
                <>
                    <DialogTitle id="dialog-title" className={classes.DialogTitleWrapper} disableTypography>
                        {props.dialogInfo.title}
                    </DialogTitle>
                    <DialogContent data-dialog-content="visible">
                        {description.length !== 0 && (
                            <div className={classes.DialogContentWrapper}>
                                {description.map((desc, index) => (
                                    <Typography key={index}>{desc}</Typography>
                                ))}
                            </div>
                        )}
                        {props.children}
                        {hasFileDrop && (
                            <div {...getRootProps({className: classes.Dropzone})} onClick={open}>
                                <input {...getInputProps()} />
                                <div className={classes.SelectBtn} >
                                    <InsertDriveFileIcon />
                                </div>
                                {importedFileName && <span>{importedFileName}</span>}
                            </div>
                        )}
                    </DialogContent>

                    <DialogActions className={classes.ActionBtnWrapper}>
                        {props.dialogInfo.customBtns || (
                            <>
                                <Button
                                    onClick={props.onConfirmBtnClick}
                                    buttonType={props.dialogInfo.buttonType || 'task'}
                                    btnText={props.dialogInfo.confirmBtn!}
                                    disabled={props.confirmDisabled}
                                    data-cy-btn="dialogConfirm"
                                />
                                {!props.dialogInfo.hideCancelBtn && (
                                    <Button
                                        className={classes.CloseBtnWrapper}
                                        onClick={props.onCancelBtnClick}
                                        buttonType={props.dialogInfo.isRemove ? 'danger' : 'default'}
                                        btnText={props.dialogInfo.cancelBtn!}
                                        data-cy-btn="dialogclose"
                                    />
                                )}
                            </>
                        )}
                    </DialogActions>
                </>
            )}
        </DialogElement>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        DialgWrapper: {
            overflow: 'auto',
        },
        DialgPaperWrapper: {
            maxHeight: 'unset',
            minWidth: 450,
            /* Aview Common Style */
            borderRadius: 0,
            boxShadow: 'none',
            backgroundColor: '#303030',
        },
        DialogContentWrapper: {
            paddingTop: `${theme.spacing()}px`,
        },
        DialogTitleWrapper: {
            color: '#fff',
            padding: '8px',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: `${theme.spacing()}px`,
            borderBottom: `1px solid ${GREY_COLOR}`,
        },
        ActionBtnWrapper: {
            marginRight: '8px',
            marginBottom: `${theme.spacing()}px`,
        },
        CloseBtnWrapper: {
            marginLeft: `${theme.spacing()}px`,
        },
        Dropzone: {
            border: '1px solid white',
            borderRadius: '5px',
            height: 'fit-content',
            marginTop: '10px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&>span': { marginLeft: '20px' }
        },
        SelectBtn: {
            padding: '10px',
            display: 'flex'
        }
    })

export default withStyles(styles, { withTheme: true })(Dialog)
